// retryTransient — retry a request the SERVER failed to answer, and only that.
//
// Observed 2026-09-01 while saving a look: `POST /api/characters/ani/blueprint`
// came back 502, and a couple of manual retries later it succeeded. Measured
// from outside at the same time, the asset-manager was up (`/health` 200 in
// ~0.9s) but the blueprint endpoint itself was slow — 1.7-4.7s for a READ of one
// character — and the POST does strictly more work (compose the document, fan
// out to the per-kind blueprint_assets rows, append a history row). So the 502 is
// that write occasionally outrunning the gateway's upstream timeout. It is a
// server-side performance problem; this only stops it reaching the user as a
// failed save they have to notice and repeat by hand.
//
// This is NOT a silent fallback (CLAUDE.md's rule): nothing is faked, no
// substitute data is returned, and when the attempts run out the real error is
// thrown with the server's own message. The only thing that changes is how many
// times we ask before believing it.
//
// WHICH failures qualify is the whole design. Retrying is safe here because a
// blueprint POST REPLACES the stored document — sending it twice lands the same
// state — and it is worth doing only when the failure says nothing about the
// request. A 4xx does: the ENG-991 guard's 400 ("this would empty body") is a
// considered refusal, and asking again just spams the server with a request it
// has already judged. So only the statuses that mean "the server did not answer"
// are retried.

/** Statuses that mean the server never got to judge the request. */
const TRANSIENT = new Set([
  408, // request timeout
  429, // rate limited — the one 4xx that is explicitly "ask again later"
  502, // bad gateway: the proxy could not reach the app (the observed one)
  503, // service unavailable
  504, // gateway timeout
])

/**
 * Is this error worth asking again about?
 *
 * Reads `err.status` — apiClient's ApiError carries the real number. A thrown
 * TypeError from `fetch` (DNS, connection refused, offline) has no status and is
 * also transient, so it qualifies too.
 */
export function isTransient(err) {
  if (!err) return false
  if (typeof err.status === 'number') return TRANSIENT.has(err.status)
  // A network-layer failure never reached the server at all.
  return err.name === 'TypeError' || /fetch|network/i.test(String(err.message ?? ''))
}

/**
 * Call `fn`, retrying only transient failures, with a widening delay.
 *
 * @param {() => Promise<T>} fn        the request, called fresh each attempt
 * @param {object} [opts]
 * @param {number} [opts.attempts=3]   total attempts, including the first
 * @param {number} [opts.baseDelayMs=500]  first backoff; doubles each retry
 * @param {(ms:number)=>Promise<void>} [opts.sleep]  injectable for tests
 * @param {(info:{attempt:number,error:Error})=>void} [opts.onRetry]
 * @returns {Promise<T>} — or throws the LAST error, unchanged
 */
export async function retryTransient(fn, {
  attempts = 3,
  baseDelayMs = 500,
  sleep = (ms) => new Promise(r => setTimeout(r, ms)),
  onRetry = null,
} = {}) {
  let lastErr
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      // A judged refusal, or the final attempt: give the caller the real error.
      if (!isTransient(err) || attempt === attempts) throw err
      onRetry?.({ attempt, error: err })
      // Widening, so a server that is briefly overloaded is not hammered at a
      // fixed rate by every client that happens to be saving.
      await sleep(baseDelayMs * 2 ** (attempt - 1))
    }
  }
  throw lastErr
}
